/** A specialist's "Documented obligations" card on a project: for each clause list in
 * force, a checklist of the clauses it assesses, read by the document's goals and then by
 * topic (each row naming its section), with
 * what is wrong or still owed first and the verdict behind every status. See
 * docs/Y2026-Q4/design-document-registers.md section 4.19a. */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useProjectRegisters } from "../api";
import type {
  DocumentRegister,
  LineRef,
  LineStatus,
  ProjectRegisterLine,
  ProjectRegisters,
  RegisterTopic,
} from "../api";
import { useAgentLabel } from "../api/hooks/useStatefulAgents";
import { clausePagePath, documentClauseAnchor } from "../routes/learningAgentPaths";
import { useScrollToHash } from "../utils/useScrollToHash";
import { ClauseFilterBar } from "./ClauseFilters";
import {
  type FilterDim,
  filterClauses,
  groupDim,
  useClauseFilters,
} from "./clauseFilterModel";
import { Band, ClauseGroups, CollapseScope, type RowContext } from "./ClauseGroups";
import {
  ClauseDetails,
  ClauseLabels,
  ClauseRow,
  CountsLine,
  ListItems,
  StatusLegend,
  StatusSymbol,
} from "./ClauseParts";
import { ClauseVerdict } from "./ClauseVerdict";
import styles from "./Clauses.module.css";
import {
  BAND_TITLE,
  type Band as BandKey,
  byProblem,
  countStatuses,
  dueText,
  KIND_LABEL,
  KIND_OF,
  KIND_SYMBOL,
  STATUS_KINDS,
  groupOf,
  isIrrelevant,
  itemCountsText,
  listGoalOf,
  rowTitle,
  STATUS_LABEL,
  type StatusKind,
  standingOf,
  topicGrouping,
} from "./clauseStatus";
import { SectionBlock, SectionCard } from "./SpecialistRowSections";

type AgentClauses = ProjectRegisters["by_agent"][string];

const NO_CONTEXT: RowContext = { topic: null, form: null, assessedBy: null };

// The clauses routed to it that it does not assess, each in its band.
const LISTS: ["dependencies" | "principles" | "conflicts" | "awaiting_documents", BandKey][] = [
  ["dependencies", "dependencies"],
  ["principles", "principles"],
  ["conflicts", "conflicts"],
  ["awaiting_documents", "awaiting"],
];

const DocumentBlock = ({
  register,
  projectId,
  agent,
  mine,
  all,
  kept,
  agentLabel,
  topicLabel,
  topics,
}: {
  register: DocumentRegister;
  projectId: string;
  agent: string;
  mine: AgentClauses;
  /** Every clause it assesses in this document, for the summary. */
  all: ProjectRegisterLine[];
  /** The clauses of this document the filters keep, assessed or listed. */
  kept: Set<string>;
  agentLabel: (agent: string) => string;
  topicLabel: (key: string) => string;
  topics: RegisterTopic[];
}) => {
  const doc = register.document_id;
  const of = (lines: ProjectRegisterLine[]) =>
    lines.filter((l) => l.document_id === doc && kept.has(l.line_id));
  const lines = of(mine.lines);
  const clausePage = clausePagePath(register, projectId);
  const grouping = topicGrouping(mine.goals[doc] ?? [], topics, agent);
  // Its own status on a clause other specialists also assess.
  const statusOf = (line: ProjectRegisterLine): LineStatus =>
    line.by_agent.find((a) => a.agent === agent)?.status ?? line.status;
  const otherOptions = (line: ProjectRegisterLine): LineRef[] =>
    line.one_of
      ? all.filter((l) => l.one_of === line.one_of && l.line_id !== line.line_id)
      : [];
  const row = (line: ProjectRegisterLine, _context: RowContext = NO_CONTEXT) => {
    const seen = line.by_agent.find((a) => a.agent === agent) ?? {
      agent,
      status: line.status,
      moved: line.moved,
      assessment: line.assessment,
    };
    const assessed = line.standing === null;
    return (
      <ClauseRow
        key={`${line.document_id}-${line.position}`}
        line={line}
        marker={assessed ? <StatusSymbol status={seen.status} /> : null}
        title={rowTitle(line)}
        labels={
          <ClauseLabels
            line={line}
            options={otherOptions(line).length + 1}
            agentLabel={agentLabel}
            showSection
            viewer={agent}
          />
        }
        below={
          line.entry === "list" ? (
            <ListItems
              entry={line}
              goal={listGoalOf(line, grouping.goals)}
              lineOf={() => undefined}
              itemOf={(id) => seen.assessment?.items?.[id] ?? null}
              details={() => null}
            />
          ) : undefined
        }
        trailing={
          line.item_counts ? (
            <span className={styles.muted}>{itemCountsText(line.item_counts)}</span>
          ) : seen.status === "not_due" || line.moved.length > 0 ? (
            <span className={styles.muted}>
              {seen.status === "not_due" ? `Due ${dueText(seen.assessment)}` : ""}
              {seen.status === "not_due" && line.moved.length > 0 ? " · " : ""}
              {line.moved.length > 0 ? "a cited document changed" : ""}
            </span>
          ) : null
        }
        details={() => (
          <ClauseDetails
            line={line}
            otherOptions={otherOptions(line)}
            definitionTexts={mine.definition_texts}
            refHref={(id) => `${clausePage}#${id}`}
            agentLabel={agentLabel}
            topicLabel={topicLabel}
            why={assessed ? <ClauseVerdict seen={seen} agentLabel={agentLabel} projectId={projectId} /> : null}
          />
        )}
      />
    );
  };
  const grouped = (members: ProjectRegisterLine[]) => (
    <ClauseGroups
      lines={members}
      grouping={grouping}
      statusOf={statusOf}
      renderLine={row}
      sourceHref={(id) => `${clausePage}?view=document#${id}`}
    />
  );
  // A package of documents stays whole, in the band of its worst document.
  const packageBand = new Map(
    grouping.goals
      .filter((g) => g.kind === "package")
      .map((g) => {
        const members = lines.filter((l) => groupOf(l, grouping) === g.key);
        const worst = members.map(statusOf).sort(byProblem)[0];
        return [g.key, worst ? standingOf(worst) : "settled"] as const;
      }),
  );
  const bandOf = (line: ProjectRegisterLine) =>
    packageBand.get(groupOf(line, grouping)) ?? standingOf(statusOf(line));
  const problems = lines
    .filter((l) => bandOf(l) === "problem")
    .sort((a, b) => byProblem(statusOf(a), statusOf(b)));
  const unassessed = lines.filter((l) => bandOf(l) === "unassessed");
  const notDue = lines.filter((l) => bandOf(l) === "not_due");
  const settled = lines.filter((l) => bandOf(l) === "settled");
  const lists = LISTS.map(([key, band]) => [BAND_TITLE[band], of(mine[key])] as const).filter(
    ([, members]) => members.length > 0,
  );
  const empty = lines.length === 0 && lists.length === 0;
  return (
    <div className={styles.document} id={documentClauseAnchor(doc)}>
      <div className={styles.documentHead}>
        <Link to={clausePage} className={styles.reference}>
          {register.document_name}
        </Link>{" "}
        {register.slot && <span className={styles.chip}>{register.slot.role_label}</span>}{" "}
        <CountsLine
          counts={countStatuses(all.map(statusOf))}
          empty="Nothing it assesses"
        />
      </div>
      {empty ? (
        <p className={styles.muted}>No clause of this document matches the filters.</p>
      ) : (
        <>
          {problems.length > 0
            ? grouped(problems)
            : lines.length > 0 && (
                <p className={styles.muted}>Nothing shown deviates or is still owed.</p>
              )}
          {unassessed.length > 0 && (
            <Band
              title="Not assessed yet"
              count={unassessed.length}
              holds={new Set(unassessed.map((l) => l.line_id))}
            >
              {grouped(unassessed)}
            </Band>
          )}
          {notDue.length > 0 && (
            <Band
              title={STATUS_LABEL.not_due}
              count={notDue.length}
              holds={new Set(notDue.map((l) => l.line_id))}
            >
              {grouped(notDue)}
            </Band>
          )}
          {settled.length > 0 && (
            <Band
              title="Met or not applicable"
              count={settled.length}
              holds={new Set(settled.map((l) => l.line_id))}
            >
              {grouped(settled)}
            </Band>
          )}
          {lists.map(([title, members]) => (
            <Band
              key={title}
              title={title}
              count={members.length}
              holds={new Set(members.map((l) => l.line_id))}
            >
              <ul className={styles.list}>{members.map((l) => row(l))}</ul>
            </Band>
          ))}
        </>
      )}
    </div>
  );
};

export const DocumentClausesCard = ({
  projectId,
  agent,
}: {
  projectId: string;
  agent: string;
}) => {
  const { data, error } = useProjectRegisters(projectId, agent);
  const { agentLabel } = useAgentLabel();
  const [filters, setFilters] = useClauseFilters();
  const [problemsOnly, setProblemsOnly] = useState(false);
  useScrollToHash(!!data);
  const mine = data?.by_agent[agent];
  const documents = useMemo(
    () =>
      (data?.registers ?? []).filter((r) =>
        [mine?.lines ?? [], ...LISTS.map(([key]) => mine?.[key] ?? [])].some((lines) =>
          lines.some((l) => l.document_id === r.document_id),
        ),
      ),
    [data, mine],
  );
  const everything = useMemo(
    () =>
      mine
        ? [mine.lines, ...LISTS.map(([key]) => mine[key])].flat().filter((l) => !isIrrelevant(l))
        : [],
    [mine],
  );
  const dims = useMemo((): FilterDim<ProjectRegisterLine>[] => {
    const name = new Map(documents.map((r) => [r.document_id, r.document_name]));
    const statusDim: FilterDim<ProjectRegisterLine> = {
      key: "status",
      noun: { one: "status", many: "statuses" },
      // A clause it does not assess has no status to filter on.
      values: (l) =>
        l.standing === null
          ? [KIND_OF[l.by_agent.find((a) => a.agent === agent)?.status ?? l.status]]
          : [],
      labelOf: (v) => `${KIND_SYMBOL[v as StatusKind]} ${KIND_LABEL[v as StatusKind]}`,
      order: STATUS_KINDS,
    };
    return [
      statusDim,
      {
        key: "document",
        noun: { one: "document", many: "documents" },
        values: (l) => [l.document_id],
        labelOf: (v) => name.get(v) ?? v,
      },
      groupDim(
        topicGrouping(Object.values(mine?.goals ?? {}).flat(), data?.topics ?? [], agent),
      ),
      {
        key: "topic",
        noun: { one: "topic", many: "topics" },
        values: (l) => l.topics,
        labelOf: (v) => data?.topics.find((t) => t.key === v)?.label ?? v,
      },
    ];
  }, [documents, mine, data, agent]);
  if (error) {
    return (
      <SectionCard title="Documented obligations" aside={null}>
        <SectionBlock>
          <p className={styles.muted}>Could not load the documented obligations.</p>
        </SectionBlock>
      </SectionCard>
    );
  }
  // An agent no topic routes to never assesses a clause, so the card would only ever
  // say so.
  if (
    !data ||
    data.enabled_roles.length === 0 ||
    !data.topics.some((t) => t.assessor === agent)
  )
    return null;
  const topicLabel = (key: string) => data.topics.find((t) => t.key === key)?.label ?? key;
  const kept = filterClauses(everything, dims, filters).filter(
    (l) =>
      !problemsOnly ||
      (l.standing === null &&
        standingOf(l.by_agent.find((a) => a.agent === agent)?.status ?? l.status) === "problem"),
  );
  const keptIds = new Set(kept.map((l) => l.line_id));
  return (
    <SectionCard title="Documented obligations" aside={null} id="documented-obligations">
      <SectionBlock>
        {!mine || documents.length === 0 ? (
          <p className={styles.muted}>
            No document in force on this project sets obligations {agentLabel(agent)} assesses.
          </p>
        ) : (
          <>
            <ClauseFilterBar
              lines={everything}
              dims={dims}
              state={filters}
              onChange={setFilters}
              shown={kept.length}
            />
            <StatusLegend />
            <CollapseScope
              extra={
                <label className={styles.problemsOnly}>
                  <input
                    type="checkbox"
                    checked={problemsOnly}
                    onChange={(e) => setProblemsOnly(e.target.checked)}
                  />{" "}
                  Problems only
                </label>
              }
            >
              {documents.map((r) => (
                <DocumentBlock
                  key={r.document_id}
                  register={r}
                  projectId={projectId}
                  agent={agent}
                  mine={mine}
                  all={mine.lines.filter((l) => l.document_id === r.document_id)}
                  kept={keptIds}
                  agentLabel={agentLabel}
                  topicLabel={topicLabel}
                  topics={data.topics}
                />
              ))}
            </CollapseScope>
          </>
        )}
      </SectionBlock>
    </SectionCard>
  );
};
